import React, { useRef } from "react";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Eye, Lock } from "lucide-react";

export const BESOIN_DOC_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif,.xlsx,.xls,.csv,.dwg,.dxf";
export const BESOIN_DOC_MAX_MB = 20;
export const BESOIN_DOC_MAX_COUNT = 15;
export const BESOIN_DOC_FORMATS_HINT = "PDF, Excel/CSV, JPG/PNG/HEIC, DWG/DXF · 20 Mo max par fichier · 15 fichiers par chantier";

export const humanSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);
export const docKind = (name = "") => {
  const e = name.toLowerCase().split(".").pop();
  if (e === "pdf") return "PDF";
  if (["xlsx", "xls", "csv"].includes(e)) return "Tableur";
  if (["jpg", "jpeg", "png", "heic", "heif"].includes(e)) return "Photo";
  if (["dwg", "dxf"].includes(e)) return "Plan CAO";
  return e.toUpperCase();
};

/**
 * Sélection de pièces jointes AVANT publication : liste locale avec visibilité par fichier.
 * L'envoi réel se fait après la création du chantier (uploadPendingDocs).
 */
export const PendingDocsPicker = ({ files, setFiles }) => {
  const ref = useRef(null);
  const add = (e) => {
    const picked = Array.from(e.target.files || []); e.target.value = "";
    const next = [...files];
    for (const f of picked) {
      if (next.length >= BESOIN_DOC_MAX_COUNT) { toast.error(`Maximum ${BESOIN_DOC_MAX_COUNT} fichiers par chantier`); break; }
      if (f.size > BESOIN_DOC_MAX_MB * 1048576) { toast.error(`${f.name} : trop volumineux (20 Mo max)`); continue; }
      next.push({ file: f, category: "public" });
    }
    setFiles(next);
  };
  const setCat = (i, category) => setFiles(files.map((x, k) => (k === i ? { ...x, category } : x)));
  return (
    <div className="pt-4 border-t border-[#E4E7EB]" data-testid="pending-docs-picker">
      <label className="label">Pièces jointes <span className="font-normal text-[#3E4C59]">— descriptif, quantitatif, photos, plans</span></label>
      <input ref={ref} type="file" multiple accept={BESOIN_DOC_ACCEPT} className="hidden" onChange={add} data-testid="pending-docs-input" />
      <button type="button" className="btn-outline" onClick={() => ref.current?.click()} data-testid="pending-docs-add">
        <Upload size={14} /> Joindre des fichiers ({files.length}/{BESOIN_DOC_MAX_COUNT})
      </button>
      <p className="text-xs text-[#3E4C59] mt-1">{BESOIN_DOC_FORMATS_HINT}. Les photos sont nettoyées de leurs données de localisation à l'envoi.</p>
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((x, i) => (
            <li key={i} className="flex items-center gap-3 p-2 border border-[#E4E7EB] rounded-[4px] bg-white" data-testid={`pending-doc-${i}`}>
              <FileText size={16} className="text-[#1B3A5C] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#1F2933] truncate">{x.file.name}</div>
                <div className="text-xs text-[#3E4C59]">{docKind(x.file.name)} · {humanSize(x.file.size)}</div>
              </div>
              <select className="select text-xs w-auto" value={x.category} onChange={(e) => setCat(i, e.target.value)} data-testid={`pending-doc-visibility-${i}`}>
                <option value="public">Visible par les artisans intéressés</option>
                <option value="contract">Réservé au sous-traitant retenu</option>
              </select>
              <button type="button" className="btn-ghost p-1 text-[#C0392B]" onClick={() => setFiles(files.filter((_, k) => k !== i))} aria-label="Retirer" data-testid={`pending-doc-remove-${i}`}><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export async function uploadPendingDocs(api, besoinId, files) {
  let ok = 0; const errors = [];
  for (const x of files) {
    const fd = new FormData(); fd.append("category", x.category); fd.append("file", x.file);
    try { await api.post(`/besoins/${besoinId}/docs`, fd, { headers: { "Content-Type": "multipart/form-data" } }); ok += 1; }
    catch (e) { errors.push(`${x.file.name} : ${e?.response?.data?.detail || "échec"}`); }
  }
  return { ok, errors };
}

export const VisibilityBadge = ({ category }) => category === "contract"
  ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#7C3E00] bg-[#FFF4EA] border border-[#E8710A]/30 px-1.5 py-0.5 rounded-[3px]"><Lock size={10} /> Réservé au retenu</span>
  : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#1B3A5C] bg-[#F0F4F8] border border-[#1B3A5C]/15 px-1.5 py-0.5 rounded-[3px]"><Eye size={10} /> Artisans intéressés</span>;
